import { Link } from '@tanstack/react-router'
import { type ReactNode, useEffect, useRef } from 'react'
import { mono, SLink } from './ui'

export const inputCls =
  'h-7 w-full rounded-[4px] border border-[var(--color-border)] bg-[var(--color-bg)] px-2 text-[12px] text-[var(--color-fg)] placeholder:text-[var(--color-muted)] focus:border-[var(--color-accent)] focus:outline-none disabled:cursor-not-allowed disabled:opacity-50'
export const th =
  'h-7 border-b border-[var(--color-border)] px-3 text-left text-[11px] font-medium uppercase tracking-[0.06em] text-[var(--color-muted)]'
export const td = 'h-7 truncate border-b border-[var(--color-border)] px-3 text-[12px]'

type Variant = 'primary' | 'ghost' | 'danger'

export function Btn({
  children,
  onClick,
  type = 'button',
  variant = 'ghost',
  disabled,
  title,
}: {
  children: ReactNode
  onClick?: () => void
  type?: 'button' | 'submit'
  variant?: Variant
  disabled?: boolean
  title?: string
}) {
  const v =
    variant === 'primary'
      ? 'border-transparent bg-[var(--color-accent)] font-medium text-[var(--color-accent-fg)] hover:opacity-90'
      : variant === 'danger'
        ? 'border-[var(--color-border)] text-[var(--v2-bad)] hover:bg-[var(--color-surface-2)]'
        : 'border-[var(--color-border)] hover:bg-[var(--color-surface-2)]'
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`inline-flex h-6 shrink-0 items-center rounded-[4px] border px-2 text-[12px] focus-visible:outline-1 focus-visible:outline-[var(--color-accent)] disabled:cursor-not-allowed disabled:opacity-40 ${v}`}
    >
      {children}
    </button>
  )
}

export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: the control is passed as children inside this label
    <label className="block">
      <span className="mb-1 flex items-baseline gap-2 text-[11px] uppercase tracking-[0.06em] text-[var(--color-muted)]">
        {label}
        {hint && <span className="normal-case tracking-normal opacity-80">{hint}</span>}
      </span>
      {children}
    </label>
  )
}

export function Select({
  value,
  onChange,
  options,
  placeholder,
  label,
}: {
  value: string
  onChange: (v: string) => void
  options: [string, string][]
  placeholder?: string
  label?: string
}) {
  return (
    <select aria-label={label} className={inputCls} value={value} onChange={(e) => onChange(e.target.value)}>
      {placeholder ? <option value="">{placeholder}</option> : null}
      {options.map(([v, l]) => (
        <option key={v} value={v}>
          {l}
        </option>
      ))}
    </select>
  )
}

/** Inline result line; errors are role=alert. */
export function Inline({ tone, children }: { tone: 'ok' | 'bad' | 'muted' | 'warn'; children: ReactNode }) {
  const color =
    tone === 'ok'
      ? 'var(--v2-ok)'
      : tone === 'bad'
        ? 'var(--v2-bad)'
        : tone === 'warn'
          ? 'var(--v2-warn)'
          : 'var(--color-muted)'
  return (
    <p role={tone === 'bad' ? 'alert' : 'status'} className="text-[12px]" style={{ ...mono, color }}>
      {children}
    </p>
  )
}

export function Chip({ children, tone }: { children: ReactNode; tone?: 'ok' | 'bad' | 'warn' | 'off' }) {
  const c =
    tone === 'ok'
      ? 'var(--v2-ok)'
      : tone === 'bad'
        ? 'var(--v2-bad)'
        : tone === 'warn'
          ? 'var(--v2-warn)'
          : 'var(--color-muted)'
  return (
    <span
      className="inline-flex h-4 items-center rounded-[3px] border px-1 text-[10px] uppercase tracking-[0.06em]"
      style={{ ...mono, color: c, borderColor: `color-mix(in srgb, ${c} 40%, transparent)` }}
    >
      {children}
    </span>
  )
}

/** Modal. Escape/backdrop closes; focus moves in; Cmd/Ctrl+Enter triggers `onSubmit` when given. */
export function Dialog({
  open,
  title,
  onClose,
  onSubmit,
  children,
  footer,
}: {
  open: boolean
  title: string
  onClose: () => void
  onSubmit?: () => void
  children: ReactNode
  footer?: ReactNode
}) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    ref.current?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) onSubmit?.()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose, onSubmit])
  if (!open) return null
  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4" style={{ background: 'rgb(0 0 0 / 0.55)' }}>
      <button type="button" aria-label="Close dialog" className="absolute inset-0 cursor-default" onClick={onClose} />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className="relative max-h-[88vh] w-full max-w-[440px] overflow-auto border border-[var(--color-border)] bg-[var(--color-surface)] outline-none"
        style={{ borderRadius: 'var(--radius)', boxShadow: 'var(--v2-popover-shadow)' }}
      >
        <div className="flex h-8 items-center border-b border-[var(--color-border)] px-3">
          <h2 className="text-[12px] font-semibold">{title}</h2>
          <span className="ml-auto text-[10px] text-[var(--color-muted)]" style={mono}>
            esc
          </span>
        </div>
        <div className="grid gap-3 px-3 py-3">{children}</div>
        {footer ? (
          <div className="flex justify-end gap-2 border-t border-[var(--color-border)] px-3 py-2">{footer}</div>
        ) : null}
      </div>
    </div>
  )
}

export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  danger,
  pending,
  error,
  onConfirm,
  onClose,
}: {
  open: boolean
  title: string
  body: ReactNode
  confirmLabel: string
  danger?: boolean
  pending?: boolean
  error?: string | null
  onConfirm: () => void
  onClose: () => void
}) {
  return (
    <Dialog
      open={open}
      title={title}
      onClose={onClose}
      footer={
        <>
          <Btn onClick={onClose} disabled={pending}>
            Cancel
          </Btn>
          <Btn variant={danger ? 'danger' : 'primary'} onClick={onConfirm} disabled={pending}>
            {pending ? 'Working...' : confirmLabel}
          </Btn>
        </>
      }
    >
      <div className="text-[12px] text-[var(--color-muted)]">{body}</div>
      {error ? <Inline tone="bad">{error}</Inline> : null}
    </Dialog>
  )
}

/** locations / <store> / [network | pos /] <page> */
export function SubCrumbs({ idOrCode, section, page }: { idOrCode: string; section: 'network' | 'pos'; page: string }) {
  const base = `/locations/${encodeURIComponent(idOrCode)}`
  const hover = 'hover:text-[var(--color-fg)]'
  return (
    <nav
      aria-label="Breadcrumb"
      className="flex items-center gap-1.5 text-[12px] text-[var(--color-muted)]"
      style={mono}
    >
      <Link to="/locations" className={hover}>
        locations
      </Link>
      <span aria-hidden="true">/</span>
      <SLink to={base} className={hover}>
        {idOrCode}
      </SLink>
      <span aria-hidden="true">/</span>
      {section === 'network' ? (
        <SLink to={`${base}/network`} className={hover}>
          network
        </SLink>
      ) : (
        <span>pos</span>
      )}
      <span aria-hidden="true">/</span>
      <span className="text-[var(--color-fg)]">{page}</span>
    </nav>
  )
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="px-3 py-8 text-center text-[12px] text-[var(--color-muted)]">{children}</p>
}

/** SSR-safe, fixed-zone timestamp. */
export function utc(iso: string | null | undefined): string {
  if (!iso) return 'never'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : `${d.toISOString().slice(0, 16).replace('T', ' ')}Z`
}
